import { ApiResponse } from './common';

export const CALLBACK_CORRELATION_ID_HEADER = 'X-Callback-Correlation-Id' as const;

export enum CallbackStatus {
  Incomplete = 'Incomplete',
  Queued = 'Queued',
  Sent = 'Sent',
  Failed = 'Failed',
}

export const CALLBACK_STATUSES = [
  CallbackStatus.Incomplete,
  CallbackStatus.Queued,
  CallbackStatus.Sent,
  CallbackStatus.Failed,
] as const;

export const CALLBACK_SUBMITTED_STATUSES = [
  CallbackStatus.Queued,
  CallbackStatus.Sent,
  CallbackStatus.Failed,
] as const;

export type CallbackSubmittedStatus = (typeof CALLBACK_SUBMITTED_STATUSES)[number];

export enum CallbackReportingGroupBy {
  Insight = 'Insight',
  Knowledgebase = 'Knowledgebase',
  Status = 'Status',
}

export interface CallbackConfig {
  enabled: boolean;
  callback_email: string | null;
  cc_email: string | null;
  description: string | null;
  callback_subtitle: string | null;
  thank_you_message: string | null;
}

export interface CallbackAdministrationSettings {
  callback_bcc_email: string;
}

export interface CallbackCorrelation {
  correlation_id: string;
}

export interface CallbackCorrelationHeaders {
  [CALLBACK_CORRELATION_ID_HEADER]: string;
}

export type CommunicationRequestHeaders = Record<string, string>;

export type CallbackCommunicationRequestHeaders = CommunicationRequestHeaders & CallbackCorrelationHeaders;

export const getCallbackCorrelationHeaders = ({
  correlation_id,
}: CallbackCorrelation): CallbackCommunicationRequestHeaders => ({
  [CALLBACK_CORRELATION_ID_HEADER]: correlation_id,
});

export interface CallbackInsightReference {
  insight_id: string;
  insight_name?: string;
  knowledgebase_id?: string;
  knowledgebase_name?: string;
}

export interface OrganisationInsightCallbackSettings {
  insight_id: string;
  knowledgebase_id?: string;
  callback: CallbackConfig | null;
}

export interface CallbackSettingsMap {
  [insight_id: string]: CallbackConfig | null;
}

export interface CallbackRequestContactDetails {
  full_name: string;
  mobile: string;
  email: string;
  message: string;
}

export interface CallbackRequestContext {
  organisation_id: string;
  insight_id: string;
  knowledgebase_id?: string;
  include_chat_history: boolean;
  chat_id?: string | null;
}

export interface UpsertCallbackRequestDraft extends CallbackRequestContactDetails, CallbackRequestContext {}

export interface SubmitCallbackRequest extends CallbackRequestContactDetails, CallbackRequestContext {
  id?: string | null;
}

export interface CallbackDraftResponse extends CallbackCorrelation {
  id: string;
  status: CallbackStatus.Incomplete;
  created_on: string;
}

export interface SubmitCallbackRequestResponse extends CallbackCorrelation {
  id: string;
  status: CallbackStatus.Queued;
  queued_on: string;
}

export interface CallbackDateRangeFilter {
  date_range?: string;
  date_from?: string | null;
  date_to?: string | null;
}

export interface CallbackPaginationFilter {
  page_number?: number;
  page_size?: number;
}

export interface CallbackLogFilter extends CallbackDateRangeFilter, CallbackPaginationFilter {
  organisation_ids?: string[];
  insight_ids?: string[];
  knowledgebase_ids?: string[];
  statuses?: CallbackStatus[];
  search?: string | null;
}

export interface CallbackLog extends CallbackInsightReference, CallbackRequestContactDetails, CallbackCorrelation {
  id: string;
  organisation_id: string;
  organisation_name: string;
  insight_id: string;
  insight_name: string;
  knowledgebase_id?: string;
  knowledgebase_name?: string;
  status: CallbackStatus;
  callback_email: string;
  cc_email: string | null;
  include_chat_history: boolean;
  chat_id: string | null;
  created_on: string;
  queued_on: string | null;
  sent_on: string | null;
  failed_on: string | null;
  error_message: string | null;
}

export interface CallbackLogResponse extends ApiResponse<CallbackLog> {}

export interface CallbackExportRequest extends CallbackLogFilter {}

export interface CallbackExportRow extends CallbackRequestContactDetails {
  correlation_id: string;
  organisation_name: string;
  insight_id?: string;
  insight_name: string;
  knowledgebase_id?: string;
  knowledgebase_name?: string;
  status: CallbackStatus;
  callback_email: string;
  cc_email: string | null;
  include_chat_history: boolean;
  chat_id: string | null;
  created_on: string;
  queued_on: string | null;
  sent_on: string | null;
  failed_on: string | null;
  error_message: string | null;
}

export interface CallbackReportingFilter extends CallbackDateRangeFilter {
  organisation_ids: string[];
  insight_ids: string[];
  knowledgebase_ids?: string[];
  date_range: string;
  statuses?: CallbackStatus[];
  group_by?: CallbackReportingGroupBy;
}

export interface CallbackReportingItem extends CallbackInsightReference {
  name?: string;
  insight_id: string;
  insight_name: string;
  count: number;
}

export interface CallbackReportingResponse {
  total: number;
  items: CallbackReportingItem[];
}
